import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface KPICardProps {
  icon: ReactNode;
  value: string | number;
  label: string;
  iconBgClass?: string;
  iconClass?: string;
  navigateTo?: string;
  onClick?: () => void;
}

export function KPICard({
  icon,
  value,
  label,
  iconBgClass = 'bg-primary/10',
  iconClass = 'text-primary',
  navigateTo,
  onClick,
}: KPICardProps) {
  const navigate = useNavigate();
  const isClickable = !!navigateTo || !!onClick;

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (navigateTo) {
      navigate(navigateTo);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <Card
      className={cn(
        'transition-all duration-200',
        isClickable && 'cursor-pointer hover:shadow-md hover:border-primary/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2'
      )}
      onClick={isClickable ? handleClick : undefined}
      onKeyDown={isClickable ? handleKeyDown : undefined}
      tabIndex={isClickable ? 0 : undefined}
      role={isClickable ? 'button' : undefined}
    >
      <CardContent className="pt-5">
        <div className="flex items-center gap-3">
          <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center', iconBgClass)}>
            <div className={cn('w-5 h-5', iconClass)}>{icon}</div>
          </div>
          <div>
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-xs text-muted-foreground">{label}</p>
          </div>
        </div>
        {isClickable && (
          <div className="mt-2 text-xs text-muted-foreground/60">
            Click to view details →
          </div>
        )}
      </CardContent>
    </Card>
  );
}
